import { Trans, useLingui } from '@lingui/react/macro';

import Badge from '@/design_system/Badge';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import IconExchange from '@/icons/Exchange.svg';
import IconPackageIssue from '@/icons/PackageIssue.svg';
import { useActivities } from '@/models/article';
import { useExchangeStockLocations, useUpdateArticleExchange } from '@/models/articleExchange';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import useDebouncedState from '@/utils/useDebouncedState';

export const StockLocationInput = () => {
  const { t } = useLingui();
  const { article, request, errors } = useArticleContext();
  const { mutate: updateArticleExchange } = useUpdateArticleExchange(article.id);

  const [query, debouncedQuery, setQuery] = useDebouncedState('', 500);

  const { data: { activities: stockRefusedActivities } = { activities: [] } } = useActivities({
    articleId: article.id,
    types: ['article_exchange_stock_availability_refused'],
  });

  const { data, isFetching } = useExchangeStockLocations({
    articleId: article.id,
    query: { search: debouncedQuery.trim() || undefined, limit: 20 },
    options: { keepPreviousData: true },
  });

  const stockLocationId = article.snapshot.articleExchange?.stockLocationId;

  const options = [...(data?.stockLocations ?? [])];
  const storeOption = options.find((option) => option.id === request.storeId);

  const sortedOptions = storeOption
    ? [storeOption, ...options.filter((option) => option.id !== request.storeId)]
    : options;

  const value = options.find((option) => option.id === stockLocationId) ?? null;

  return (
    <InputSelect
      variant="select"
      label={t({
        id: 'service-choice.exchange.stock-location.label',
        message: 'Stock location',
      })}
      aria-label={t({
        id: 'service-choice.exchange.stock-location.label',
        message: 'Stock location',
      })}
      placeholder={t({
        id: 'service-choice.exchange.stock-location.placeholder',
        message: 'Select an option',
      })}
      value={value}
      isClearable
      inputValue={query}
      onInputChange={setQuery}
      filterOption={null}
      blurInputOnSelect
      isLoading={isFetching || query !== debouncedQuery}
      options={sortedOptions}
      getOptionValue={(option) => option.id}
      formatOptionLabel={(option) => {
        const hasRefusedStockAvailability = stockRefusedActivities.some(
          (activity) =>
            activity.entityId === option.id &&
            activity.data.replacementProduct.id ===
              article.snapshot.articleExchange?.replacementProductId
        );

        return (
          <Stack row gap="0.5rem" alignItems="center">
            <span>{option.name}</span>
            {option.id === request.storeId && <OnsiteStockBadge />}
            {hasRefusedStockAvailability && <OutOfStockBadge />}
          </Stack>
        );
      }}
      onChange={(option) => updateArticleExchange({ stockLocationId: option?.id ?? null })}
      error={
        errors.services?.exchange?.missingStockLocation
          ? t({
              id: 'stock-location.errors.missing',
              message: 'Please select a stock location',
            })
          : undefined
      }
    />
  );
};

export const StockLocation = () => {
  const { article, request, view } = useArticleContext();

  const stockLocation = article.snapshot.articleExchange?.stockLocation;

  if (view.services.exchange.stockLocation.editable || !stockLocation) {
    return null;
  }

  return (
    <p>
      <InlineText color="secondary">
        •{' '}
        <Trans id="service-choice.exchange.stock-location.readonly-label">
          Stock location:
        </Trans>{' '}
      </InlineText>
      <InlineText weight="medium">
        {stockLocation.name}
        {stockLocation.id === request.storeId && (
          <>
            {/* Non breaking space, so that the tag is wrapped with the last word of the entity name */}
            {'\u00A0'}
            <OnsiteStockBadge />
          </>
        )}
      </InlineText>
    </p>
  );
};

const OnsiteStockBadge = () => {
  return (
    <Badge
      color="tertiary"
      size="small"
      hasBorder
      style={{ display: 'inline-flex', verticalAlign: 'middle' }}
    >
      <Stack row gap="0.25rem" alignItems="center">
        <IconExchange style={{ fontSize: '0.75rem' }} />
        <Trans id="service-choice.exchange.stock-location.onsite-stock">Onsite stock</Trans>
      </Stack>
    </Badge>
  );
};

const OutOfStockBadge = () => {
  return (
    <Badge color="danger" size="small" hasBorder>
      <Stack row gap="0.25rem" alignItems="center">
        <IconPackageIssue style={{ fontSize: '0.75rem' }} />
        <Trans id="service-choice.exchange.stock-location.out-of-stock">Out of stock</Trans>
      </Stack>
    </Badge>
  );
};
